import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormField } from '@/components/shared/form-field';
import {
  LocationPicker,
  type ChurchLocation,
} from '@/components/shared/location-picker';
import { MoneyDisplay } from '@/components/shared/money-display';
import { MetricMoneyCards, MoneyTotalsStack } from '@/components/shared/money-totals-stack';
import { PageHeader } from '@/components/shared/page-header';
import { PlanUpgradePrompt } from '@/components/shared/plan-upgrade-prompt';
import { StatCard } from '@/components/shared/stat-card';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import type { CountryCode } from '@/constants/countries';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { switchChurchAndOpen } from '@/lib/switch-church-workspace';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

interface BranchRow {
  id: string;
  name: string;
  campusName: string;
  slug: string;
  status: string;
  isHead: boolean;
  country: string;
  locationLatitude: number | null;
  locationLongitude: number | null;
  address: string | null;
  memberCount: number;
  givingTotalMinor: number;
  givingCurrency: string;
  givingByCurrency: Array<{ currency: string; amountMinor: number }>;
  attendanceCount: number;
}

type CampusStatus = 'active' | 'suspended' | 'banned' | 'deactivated';

type ConsolidatedMoneyRow = {
  churchName: string;
  isHead: boolean;
  totalMinor: number;
  currency: string;
  byCurrency: Array<{ currency: string; amountMinor: number }>;
};

interface ConsolidatedDashboard {
  baseCurrency: string;
  giving: {
    rows: ConsolidatedMoneyRow[];
    combined: {
      amountMinor: number;
      currency: string;
      byCurrency: Array<{ currency: string; amountMinor: number }>;
    };
  };
  attendance: {
    rows: { churchName: string; isHead: boolean; checkInCount: number }[];
    combined: { checkInCount: number };
  };
  membership: {
    rows: { churchName: string; isHead: boolean; total: number }[];
    combined: { total: number };
  };
}

export const BranchesPage = () => {
  const { t } = useTranslation('branches');
  const { t: tChurches } = useTranslation('churches');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const membership = useChurchStore((state) => state.membership);
  const switchToBranch = useChurchStore((state) => state.switchToBranch);
  const setAsHeadOffice = useChurchStore((state) => state.setAsHeadOffice);
  const refreshCurrent = useChurchStore((state) => state.refreshCurrent);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [overview, setOverview] = useState<BranchRow | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLocation, setEditLocation] = useState<ChurchLocation | null>(null);
  const [setHeadTarget, setSetHeadTarget] = useState<BranchRow | null>(null);
  const [disableTarget, setDisableTarget] = useState<BranchRow | null>(null);

  const canManage = hasPermission(membership, PERMISSIONS.BRANCHES_MANAGE);
  const canSwitch = hasPermission(membership, PERMISSIONS.BRANCHES_SWITCH);
  const canViewConsolidated = hasPermission(membership, PERMISSIONS.BRANCHES_VIEW_CONSOLIDATED);
  const isHead = church?.parentChurchId === null;

  const isCampusOpen = (row: BranchRow): boolean => row.status === 'active';

  useEffect(() => {
    if (!overview) {
      setEditing(false);
      setEditName('');
      setEditLocation(null);
      return;
    }
    setEditName(overview.campusName);
    setEditLocation(
      overview.locationLatitude != null && overview.locationLongitude != null
        ? {
            latitude: overview.locationLatitude,
            longitude: overview.locationLongitude,
            address: overview.address,
          }
        : null,
    );
    setEditing(false);
  }, [overview]);

  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    if (membership === null) return;

    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });

    if (canManage && isHead && allowsFeature('multi_branch')) {
      setCreateOpen(true);
    }
  }, [
    allowsFeature,
    canManage,
    isHead,
    membership,
    searchParams,
    setSearchParams,
  ]);

  const branches = useQuery({
    queryKey: ['branches', church?.id],
    queryFn: () => api.get<{ data: BranchRow[] }>('/branches'),
    enabled: Boolean(church && isModuleEnabled('branches')),
  });

  const consolidated = useQuery({
    queryKey: ['branches', 'consolidated', church?.id],
    queryFn: () => api.get<ConsolidatedDashboard>('/branches/consolidated'),
    enabled: Boolean(church && canViewConsolidated && isModuleEnabled('branches')),
  });

  const create = useMutation({
    mutationFn: () => api.post('/branches', { name }),
    onSuccess: () => {
      toast.success(t('saved'));
      setCreateOpen(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      void queryClient.invalidateQueries({ queryKey: ['branches', 'consolidated'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const saveCampus = useMutation({
    mutationFn: () => {
      if (!overview) throw new Error('No campus selected');
      return api.patch(`/branches/${overview.id}`, {
        name: editName.trim(),
        address: editLocation ? editLocation.address?.trim() || null : null,
        locationLatitude: editLocation?.latitude ?? null,
        locationLongitude: editLocation?.longitude ?? null,
      });
    },
    onSuccess: async () => {
      toast.success(t('saved'));
      const campusId = overview?.id;
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      void queryClient.invalidateQueries({ queryKey: ['branches', 'consolidated'] });
      if (campusId && campusId === church?.id) {
        await refreshCurrent();
      }
      setOverview((current) =>
        current
          ? {
              ...current,
              campusName: editName.trim(),
              address: editLocation?.address?.trim() || null,
              locationLatitude: editLocation?.latitude ?? null,
              locationLongitude: editLocation?.longitude ?? null,
            }
          : null,
      );
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const openCampus = async (campusId: string, campusName?: string) => {
    if (!church || campusId === church.id) return;
    setOverview(null);
    await switchChurchAndOpen({
      action: () => switchToBranch(campusId),
      navigate,
      churchName: campusName,
    });
  };

  const promoteHead = useMutation({
    mutationFn: async (campus: BranchRow) => {
      await setAsHeadOffice(campus.id);
      return campus;
    },
    onSuccess: (campus) => {
      toast.success(t('setAsHeadSuccess', { campus: campus.campusName }));
      setSetHeadTarget(null);
      setOverview(null);
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      void queryClient.invalidateQueries({ queryKey: ['branches', 'consolidated'] });
      void queryClient.invalidateQueries({ queryKey: ['my-churches'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const disableCampus = useMutation({
    mutationFn: (campus: BranchRow) => api.post(`/branches/${campus.id}/deactivate`),
    onSuccess: (_data, campus) => {
      toast.success(t('disableSuccess', { campus: campus.campusName }));
      setDisableTarget(null);
      setOverview(null);
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      void queryClient.invalidateQueries({ queryKey: ['branches', 'consolidated'] });
      void queryClient.invalidateQueries({ queryKey: ['my-churches'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const reactivateCampus = useMutation({
    mutationFn: (campus: BranchRow) => api.post(`/branches/${campus.id}/reactivate`),
    onSuccess: (_data, campus) => {
      toast.success(t('reactivateSuccess', { campus: campus.campusName }));
      setOverview((current) =>
        current?.id === campus.id ? { ...current, status: 'active' } : current,
      );
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      void queryClient.invalidateQueries({ queryKey: ['branches', 'consolidated'] });
      void queryClient.invalidateQueries({ queryKey: ['my-churches'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const statusLabel = (status: string): string => {
    switch (status as CampusStatus) {
      case 'active':
        return t('overview.statusActive');
      case 'suspended':
        return t('overview.statusSuspended');
      case 'banned':
        return t('overview.statusBanned');
      case 'deactivated':
        return t('overview.statusDeactivated');
      default:
        return status;
    }
  };

  if (!isModuleEnabled('branches')) {
    return null;
  }

  if (!allowsFeature('multi_branch')) {
    return (
      <div>
        <PageHeader title={t('title')} description={t('description')} tipId="branches.purpose" />
        <PlanUpgradePrompt title={t('title')} description={t('upgrade')} planNote="Professional" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="branches.purpose"
        headerActions={
          canManage && isHead ? (
            <Button onClick={() => setCreateOpen(true)}>{t('create')}</Button>
          ) : undefined
        }
      />

      {canViewConsolidated && consolidated.data ? (
        <section className="space-y-4">
          <div>
            <h2 className="text-heading-sm">{t('consolidated.title')}</h2>
            <Tip tipId="branches.consolidated" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <ConsolidatedCard
              title={t('consolidated.giving')}
              combined={
                consolidated.data.giving.combined.byCurrency.length > 1 ? (
                  <MoneyTotalsStack
                    items={consolidated.data.giving.combined.byCurrency}
                    size="lg"
                  />
                ) : (
                  <MoneyDisplay
                    amount={consolidated.data.giving.combined.amountMinor}
                    currency={consolidated.data.giving.combined.currency}
                    size="lg"
                  />
                )
              }
              rows={consolidated.data.giving.rows.map((row) => ({
                label: row.churchName,
                value:
                  row.byCurrency.length > 1 ? (
                    <MoneyTotalsStack items={row.byCurrency} />
                  ) : (
                    <MoneyDisplay amount={row.totalMinor} currency={row.currency} />
                  ),
              }))}
            />
            <ConsolidatedCard
              title={t('consolidated.attendance')}
              combined={consolidated.data.attendance.combined.checkInCount}
              rows={consolidated.data.attendance.rows.map((row) => ({
                label: row.churchName,
                value: row.checkInCount,
              }))}
            />
            <ConsolidatedCard
              title={t('consolidated.membership')}
              combined={consolidated.data.membership.combined.total}
              rows={consolidated.data.membership.rows.map((row) => ({
                label: row.churchName,
                value: row.total,
              }))}
            />
          </div>
        </section>
      ) : null}

      <DataTable
        rows={branches.data?.data ?? []}
        columns={
          [
            {
              id: 'campus',
              header: t('columns.campus'),
              cell: (row) => (
                <div className="flex flex-wrap items-center gap-2">
                  <span>{row.campusName}</span>
                  {row.isHead ? (
                    <Badge variant="neutral">{t('switcher.headOffice')}</Badge>
                  ) : null}
                </div>
              ),
            },
            {
              id: 'members',
              header: t('columns.members'),
              cell: (row) => row.memberCount,
              align: 'right',
            },
            {
              id: 'giving',
              header: t('columns.giving'),
              cell: (row) =>
                row.givingByCurrency.length > 1 ? (
                  <MoneyTotalsStack items={row.givingByCurrency} />
                ) : (
                  <MoneyDisplay
                    amount={row.givingTotalMinor}
                    currency={row.givingCurrency}
                  />
                ),
              align: 'right',
            },
            {
              id: 'attendance',
              header: t('columns.attendance'),
              cell: (row) => row.attendanceCount,
              align: 'right',
            },
          ] satisfies DataTableColumn<BranchRow>[]
        }
        rowKey={(row) => row.id}
        loading={branches.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('empty')}</p>}
        onRowClick={(row) => setOverview(row)}
        rowActions={(row) =>
          row.id === church?.id ? (
            <Badge variant="brand">{t('current')}</Badge>
          ) : canSwitch && isCampusOpen(row) ? (
            <Button variant="outline" size="sm" onClick={() => void openCampus(row.id, row.campusName)}>
              {row.isHead ? t('switcher.returnHead') : t('switcher.switchTo')}
            </Button>
          ) : !isCampusOpen(row) ? (
            <Badge variant="neutral">{statusLabel(row.status)}</Badge>
          ) : null
        }
        actionsHeader={tCommon('table.actions')}
      />

      <Dialog
        open={overview !== null}
        onOpenChange={(next) => {
          if (!next) setOverview(null);
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            {editing ? (
              <DialogTitle>{t('editTitle')}</DialogTitle>
            ) : overview ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <DialogTitle className="min-w-0 truncate">{overview.campusName}</DialogTitle>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {overview.id === church?.id ? (
                      <Badge variant="brand">{t('current')}</Badge>
                    ) : null}
                    <Badge variant="neutral">{statusLabel(overview.status)}</Badge>
                  </div>
                </div>
                <DialogDescription>
                  {overview.isHead
                    ? t('overview.descriptionHead', { church: overview.name })
                    : t('overview.description', { church: overview.name })}
                </DialogDescription>
              </>
            ) : (
              <DialogTitle>{t('overview.title')}</DialogTitle>
            )}
          </DialogHeader>
          {overview ? (
            editing ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (editName.trim().length < 2) return;
                  saveCampus.mutate();
                }}
              >
                <DialogBody contentClassName="flex flex-col gap-6">
                  <FormField label={t('name')}>
                    {(field) => (
                      <Input
                        id={field.id}
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                        required
                        autoFocus
                        aria-describedby={field['aria-describedby']}
                        aria-invalid={field.invalid}
                      />
                    )}
                  </FormField>
                  <FormField label={tChurches('fields.location')}>
                    {() => (
                      <LocationPicker
                        value={editLocation}
                        onChange={setEditLocation}
                        country={(overview.country as CountryCode | undefined) ?? null}
                        mapClassName="h-56"
                      />
                    )}
                  </FormField>
                </DialogBody>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                    {tCommon('action.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    loading={saveCampus.isPending}
                    disabled={editName.trim().length < 2}
                  >
                    {t('save')}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <>
                <DialogBody>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <StatCard
                      label={t('overview.members')}
                      value={formatNumber(overview.memberCount)}
                    />
                    {overview.givingByCurrency.length > 1 ? (
                      <MetricMoneyCards
                        label={t('overview.giving')}
                        items={overview.givingByCurrency}
                      />
                    ) : (
                      <StatCard
                        label={t('overview.giving')}
                        money={{
                          amount: overview.givingTotalMinor,
                          currency: overview.givingCurrency,
                        }}
                      />
                    )}
                    <StatCard
                      label={t('overview.attendance')}
                      value={formatNumber(overview.attendanceCount)}
                    />
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOverview(null)}>
                    {tCommon('action.close')}
                  </Button>
                  {canManage ? (
                    <Button variant="outline" onClick={() => setEditing(true)}>
                      {t('edit')}
                    </Button>
                  ) : null}
                  {canManage && isHead && !overview.isHead ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSetHeadTarget(overview);
                        setOverview(null);
                      }}
                    >
                      {t('setAsHead')}
                    </Button>
                  ) : null}
                  {canManage && isHead && !overview.isHead && overview.status === 'deactivated' ? (
                    <Button
                      loading={reactivateCampus.isPending}
                      onClick={() => reactivateCampus.mutate(overview)}
                    >
                      {t('reactivate')}
                    </Button>
                  ) : null}
                  {canManage && isHead && !overview.isHead && overview.status === 'active' ? (
                    <Button
                      variant="destructive"
                      onClick={() => {
                        setDisableTarget(overview);
                        setOverview(null);
                      }}
                    >
                      {t('disable')}
                    </Button>
                  ) : null}
                  {canSwitch && overview.id !== church?.id && isCampusOpen(overview) ? (
                    <Button onClick={() => void openCampus(overview.id, overview.campusName)}>
                      {overview.isHead ? t('switcher.returnHead') : t('switcher.switchTo')}
                    </Button>
                  ) : null}
                </DialogFooter>
              </>
            )
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="overflow-visible" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('createTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="branch-name">{t('name')}</Label>
              <Input
                id="branch-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button disabled={name.trim().length < 2 || create.isPending} onClick={() => create.mutate()}>
                {t('create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={setHeadTarget !== null}
        onOpenChange={(next) => {
          if (!next) setSetHeadTarget(null);
        }}
        title={t('confirmSetAsHead.title', { campus: setHeadTarget?.campusName ?? '' })}
        description={t('confirmSetAsHead.description')}
        confirmLabel={t('confirmSetAsHead.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={promoteHead.isPending}
        typeToConfirm={
          setHeadTarget
            ? {
                value: setHeadTarget.campusName,
                label: t('confirmSetAsHead.typeLabel'),
              }
            : undefined
        }
        onConfirm={() => {
          if (setHeadTarget) promoteHead.mutate(setHeadTarget);
        }}
      />

      <ConfirmDialog
        open={disableTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDisableTarget(null);
        }}
        title={t('confirmDisable.title', { campus: disableTarget?.campusName ?? '' })}
        description={t('confirmDisable.description')}
        confirmLabel={t('confirmDisable.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={disableCampus.isPending}
        destructive
        typeToConfirm={
          disableTarget
            ? {
                value: disableTarget.campusName,
                label: t('confirmDisable.typeLabel'),
              }
            : undefined
        }
        onConfirm={() => {
          if (disableTarget) disableCampus.mutate(disableTarget);
        }}
      />
    </div>
  );
};

const ConsolidatedCard = ({
  title,
  combined,
  rows,
}: {
  title: string;
  combined: ReactNode;
  rows: { label: string; value: ReactNode }[];
}) => (
  <div className="rounded-lg border border-border bg-surface p-4">
    <p className="text-caption text-fg-secondary">{title}</p>
    <p className="mt-1 text-heading-sm tabular-nums">{combined}</p>
    <ul className="mt-4 space-y-2 text-body-sm">
      {rows.map((row) => (
        <li key={row.label} className="flex items-center justify-between gap-3">
          <span className="truncate text-fg-secondary">{row.label}</span>
          <span className="tabular-nums">{row.value}</span>
        </li>
      ))}
    </ul>
  </div>
);
